import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { createEvent, getEventById, updateEvent } from '../../services/firestoreService';
import { createEventWithPaymentIntegration, EVENT_PRICING } from '../../services/eventPaymentService';
import { useNavigate, useParams } from 'react-router-dom';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';

export default function EventForm() {
  const { currentUser, hasRole } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams(); // Get event ID for editing
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    startDate: '',
    startTime: '',
    endDate: '',
    endTime: '',
    location: '',
    isVirtual: false,
    virtualLink: '',
    maxAttendees: '',
    eventType: 'Networking',
    memberPrice: '',
    nonMemberPrice: '',
    registrationDeadline: '',
    agenda: '',
    speakers: '',
    requirements: '',
    enablePayments: true,
    paymentMethods: ['stripe', 'paypal'],
    imageUrl: ''
  });

  // Load existing event data if editing
  useEffect(() => {
    if (id) {
      setIsEditing(true);
      loadEventData();
    }
  }, [id]);

  const loadEventData = async () => {
    try {
      setLoading(true);
      const eventData = await getEventById(id);
      if (eventData) {
        // Convert dates to input format
        const startDate = eventData.startDate?.toDate ? eventData.startDate.toDate() : new Date(eventData.startDate);
        const endDate = eventData.endDate?.toDate ? eventData.endDate.toDate() : new Date(eventData.endDate);
        const regDeadline = eventData.registrationDeadline?.toDate ? eventData.registrationDeadline.toDate() : null;

        setFormData({
          title: eventData.title || '',
          description: eventData.description || '',
          startDate: startDate.toISOString().split('T')[0],
          startTime: startDate.toTimeString().slice(0, 5),
          endDate: endDate.toISOString().split('T')[0],
          endTime: endDate.toTimeString().slice(0, 5),
          location: eventData.location || '',
          isVirtual: eventData.isVirtual || false,
          virtualLink: eventData.virtualLink || '',
          maxAttendees: eventData.maxAttendees?.toString() || '',
          eventType: eventData.eventType || 'Networking',
          memberPrice: eventData.pricing?.member?.toString() || eventData.ticketTypes?.member?.price?.toString() || '',
          nonMemberPrice: eventData.pricing?.nonMember?.toString() || eventData.ticketTypes?.nonMember?.price?.toString() || '',
          registrationDeadline: regDeadline ? regDeadline.toISOString().split('T')[0] : '',
          agenda: Array.isArray(eventData.agenda) ? eventData.agenda.join('\n') : eventData.agenda || '',
          speakers: Array.isArray(eventData.speakers) ? eventData.speakers.join('\n') : eventData.speakers || '',
          requirements: Array.isArray(eventData.requirements) ? eventData.requirements.join('\n') : eventData.requirements || '',
          enablePayments: eventData.enablePayments !== false,
          paymentMethods: eventData.paymentMethods || ['stripe', 'paypal'],
          imageUrl: eventData.imageUrl || ''
        });
      }
    } catch (error) {
      console.error('Error loading event data:', error);
      setError('Failed to load event data');
    } finally {
      setLoading(false);
    }
  };

  // Auto-populate pricing when event type changes (only for new events)
  useEffect(() => {
    if (!isEditing && formData.eventType && EVENT_PRICING[formData.eventType.toUpperCase()]) {
      const pricing = EVENT_PRICING[formData.eventType.toUpperCase()];
      setFormData(prev => ({
        ...prev,
        memberPrice: pricing.member.toString(),
        nonMemberPrice: pricing.nonMember.toString()
      }));
    }
  }, [formData.eventType, isEditing]);

  // Check if user is admin
  if (!hasRole('admin')) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          You don't have permission to create events. Only administrators can create events.
        </div>
      </div>
    );
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Handle image file selection
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select a valid image file');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('Image file must be less than 5MB');
        return;
      }

      setImageFile(file);
      setError('');

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Upload image to Firebase Storage
  const uploadImage = async (file) => {
    if (!file) return null;

    setUploadingImage(true);
    try {
      const storage = getStorage();
      const timestamp = Date.now();
      const fileName = `events/${timestamp}_${file.name}`; // Fixed path to match Firebase rules
      const storageRef = ref(storage, fileName);

      console.log('📤 Uploading image to:', fileName);
      console.log('🔄 Current user:', currentUser?.uid);
      console.log('🔄 User email:', currentUser?.email);

      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);

      console.log('✅ Image uploaded successfully:', downloadURL);
      return downloadURL;
    } catch (error) {
      console.error('❌ Error uploading image:', error);
      console.error('❌ Error code:', error.code);
      console.error('❌ Error message:', error.message);
      throw new Error(`Failed to upload image: ${error.message}`);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Combine date and time
      const startDateTime = new Date(`${formData.startDate}T${formData.startTime}`);
      const endDateTime = new Date(`${formData.endDate}T${formData.endTime}`);
      
      // Format date and time for display
      const formatDate = (date) => {
        return date.toLocaleDateString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      };

      const formatTime = (date) => {
        return date.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });
      };

      // Create formatted date and time strings
      const formattedDate = formatDate(startDateTime); // Date only, no time
      const formattedTime = `${formatTime(startDateTime)} - ${formatTime(endDateTime)}`;

      // Upload image if a file was selected
      let imageUrl = formData.imageUrl || '/apn-logos/APN-blue (1).png'; // Default to APN logo

      if (imageFile) {
        console.log('📤 Uploading new image...');
        imageUrl = await uploadImage(imageFile);
        console.log('✅ Image uploaded, URL:', imageUrl);
      }

      // Determine display price (use member price as default)
      const displayPrice = formData.memberPrice ?
        `$${parseFloat(formData.memberPrice).toFixed(2)}` :
        'Free';

      const eventData = {
        title: formData.title,
        description: formData.description,
        startDate: startDateTime,
        endDate: endDateTime,
        date: formattedDate, // For display on events page
        time: formattedTime, // For display on event details page
        price: displayPrice, // For display on events page
        location: formData.location,
        isVirtual: formData.isVirtual,
        virtualLink: formData.virtualLink,
        maxAttendees: formData.maxAttendees ? parseInt(formData.maxAttendees) : null,
        eventType: formData.eventType,
        imageUrl: imageUrl,
        pricing: {
          member: formData.memberPrice ? parseFloat(formData.memberPrice) : 0,
          nonMember: formData.nonMemberPrice ? parseFloat(formData.nonMemberPrice) : 0
        },
        registrationDeadline: formData.registrationDeadline ? new Date(formData.registrationDeadline) : null,
        agenda: formData.agenda.split('\n').filter(item => item.trim()),
        speakers: formData.speakers.split('\n').filter(speaker => speaker.trim()),
        requirements: formData.requirements.split('\n').filter(req => req.trim()),
        enablePayments: formData.enablePayments,
        paymentMethods: formData.paymentMethods,
        createdBy: currentUser.uid,
        // Enhanced attendee management
        attendeeList: {
          enabled: true,
          exportFormats: ['excel', 'csv'],
          includePaymentInfo: true,
          autoEmailReminders: true,
          customFields: ['dietary_restrictions', 'accessibility_needs', 'company_size']
        },
        // Automatic payment integration
        paymentIntegration: {
          stripe: {
            enabled: formData.enablePayments && formData.paymentMethods.includes('stripe'),
            autoCreateProducts: true,
            webhookEnabled: true,
            autoRefunds: true
          },
          paypal: {
            enabled: formData.enablePayments && formData.paymentMethods.includes('paypal'),
            autoCreateProducts: true,
            webhookEnabled: true,
            autoRefunds: true
          }
        },
        // Notification settings
        notifications: {
          newRegistration: true,
          paymentReceived: true,
          eventReminders: true,
          attendeeUpdates: true,
          adminAlerts: true
        },
        // Workshop/Virtual event specific settings
        eventSettings: {
          allowWaitlist: true,
          sendCalendarInvites: true,
          recordSession: formData.isVirtual,
          breakoutRooms: formData.isVirtual,
          networkingTime: true
        }
      };

      // Add date validation
      if (startDateTime >= endDateTime) {
        setError('End date must be after start date');
        setLoading(false);
        return;
      }

      let eventId;

      if (isEditing) {
        // Update existing event
        await updateEvent(id, eventData);
        eventId = id;

        // Show update success message
        alert(`✅ Event Updated Successfully!\n\nTitle: ${formData.title}\n\nThe event has been updated with your changes.`);
      } else {
        // Create new event with automatic payment integration
        eventId = formData.enablePayments
          ? await createEventWithPaymentIntegration(eventData)
          : await createEvent(eventData);

        // Show comprehensive success message
        const successMessage = `✅ Event Created Successfully!

Event ID: ${eventId}
Title: ${formData.title}

🔧 Automatic Setup Completed:
${formData.enablePayments ? '✅ Payment integration (Stripe/PayPal) configured' : '⚠️ Payment integration disabled'}
✅ Attendee list management enabled
✅ Excel/CSV export ready
✅ Email notifications activated
✅ Calendar integration prepared

📊 Next Steps:
• Review event details
• Test registration process
• Monitor attendee registrations
• Export attendee lists when needed`;

        alert(successMessage);
      }

      navigate(`/events/${eventId}`);
    } catch (error) {
      const errorMessage = error.message || `Failed to ${isEditing ? 'update' : 'create'} event. Please try again.`;
      setError(`Event ${isEditing ? 'Update' : 'Creation'} Failed: ${errorMessage}`);
      console.error(`Error ${isEditing ? 'updating' : 'creating'} event:`, error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">
            {isEditing ? 'Edit Event' : 'Create New Event'}
          </h2>
          <p className="mt-2 text-gray-600">
            {isEditing ? 'Update event details and settings.' : 'Create and manage events for the APN community.'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          {/* Basic Information */}
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700">
              Event Title *
            </label>
            <input
              type="text"
              id="title"
              name="title"
              required
              value={formData.title}
              onChange={handleChange}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-apn-blue focus:border-apn-blue"
              placeholder="e.g., APN Annual Networking Event"
            />
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700">
              Event Description *
            </label>
            <textarea
              id="description"
              name="description"
              required
              rows="4"
              value={formData.description}
              onChange={handleChange}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-apn-blue focus:border-apn-blue"
              placeholder="Describe the event, its purpose, and what attendees can expect..."
            />
          </div>

          {/* Event Image */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Event Image
            </label>

            {/* Image Upload Option */}
            <div className="mb-4">
              <label htmlFor="imageFile" className="block text-sm font-medium text-gray-600 mb-2">
                Upload Image File
              </label>
              <input
                type="file"
                id="imageFile"
                accept="image/*"
                onChange={handleImageChange}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-apn-blue file:text-white hover:file:bg-blue-700"
              />
              <p className="mt-1 text-sm text-gray-500">
                Upload an image file (JPG, PNG, GIF). Max size: 5MB.
              </p>
            </div>

            {/* Image Preview */}
            {imagePreview && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-600 mb-2">
                  Image Preview
                </label>
                <img
                  src={imagePreview}
                  alt="Event preview"
                  className="w-full max-w-md h-48 object-cover rounded-lg border border-gray-300"
                />
              </div>
            )}

            {/* OR Divider */}
            <div className="flex items-center my-4">
              <div className="flex-grow border-t border-gray-300"></div>
              <span className="px-3 text-sm text-gray-500 bg-white">OR</span>
              <div className="flex-grow border-t border-gray-300"></div>
            </div>

            {/* Image URL Option */}
            <div>
              <label htmlFor="imageUrl" className="block text-sm font-medium text-gray-600 mb-2">
                Image URL
              </label>
              <input
                type="text"
                id="imageUrl"
                name="imageUrl"
                value={formData.imageUrl}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-apn-blue focus:border-apn-blue"
                placeholder="https://example.com/event-image.jpg"
              />
              <p className="mt-1 text-sm text-gray-500">
                Or provide a URL to an existing image. If both file and URL are provided, the uploaded file will be used.
              </p>
            </div>

            {/* Default Image Notice */}
            {!imageFile && !formData.imageUrl && (
              <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
                <p className="text-sm text-blue-700">
                  📷 No image selected. The APN logo will be used as the default event image.
                </p>
              </div>
            )}
          </div>

          {/* Date and Time */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="startDate" className="block text-sm font-medium text-gray-700">
                Start Date *
              </label>
              <input
                type="date"
                id="startDate"
                name="startDate"
                required
                value={formData.startDate}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-apn-blue focus:border-apn-blue"
              />
            </div>

            <div>
              <label htmlFor="startTime" className="block text-sm font-medium text-gray-700">
                Start Time *
              </label>
              <input
                type="time"
                id="startTime"
                name="startTime"
                required
                value={formData.startTime}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-apn-blue focus:border-apn-blue"
              />
            </div>

            <div>
              <label htmlFor="endDate" className="block text-sm font-medium text-gray-700">
                End Date *
              </label>
              <input
                type="date"
                id="endDate"
                name="endDate"
                required
                value={formData.endDate}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-apn-blue focus:border-apn-blue"
              />
            </div>

            <div>
              <label htmlFor="endTime" className="block text-sm font-medium text-gray-700">
                End Time *
              </label>
              <input
                type="time"
                id="endTime"
                name="endTime"
                required
                value={formData.endTime}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-apn-blue focus:border-apn-blue"
              />
            </div>
          </div>

          {/* Location */}
          <div>
            <div className="flex items-center mb-4">
              <input
                type="checkbox"
                id="isVirtual"
                name="isVirtual"
                checked={formData.isVirtual}
                onChange={handleChange}
                className="h-4 w-4 text-apn-blue focus:ring-apn-blue border-gray-300 rounded"
              />
              <label htmlFor="isVirtual" className="ml-2 block text-sm text-gray-900">
                This is a virtual event
              </label>
            </div>

            {formData.isVirtual ? (
              <div>
                <label htmlFor="virtualLink" className="block text-sm font-medium text-gray-700">
                  Virtual Event Link *
                </label>
                <input
                  type="url"
                  id="virtualLink"
                  name="virtualLink"
                  required={formData.isVirtual}
                  value={formData.virtualLink}
                  onChange={handleChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-apn-blue focus:border-apn-blue"
                  placeholder="https://zoom.us/j/..."
                />
              </div>
            ) : (
              <div>
                <label htmlFor="location" className="block text-sm font-medium text-gray-700">
                  Event Location *
                </label>
                <input
                  type="text"
                  id="location"
                  name="location"
                  required={!formData.isVirtual}
                  value={formData.location}
                  onChange={handleChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-apn-blue focus:border-apn-blue"
                  placeholder="e.g., 123 Main St, New York, NY 10001"
                />
              </div>
            )}
          </div>

          {/* Event Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="eventType" className="block text-sm font-medium text-gray-700">
                Event Type *
              </label>
              <select
                id="eventType"
                name="eventType"
                required
                value={formData.eventType}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-apn-blue focus:border-apn-blue"
              >
                <option value="Networking">Networking</option>
                <option value="Workshop">Workshop</option>
                <option value="Seminar">Seminar</option>
                <option value="Conference">Conference</option>
                <option value="Social">Social Event</option>
                <option value="Training">Training</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div>
              <label htmlFor="maxAttendees" className="block text-sm font-medium text-gray-700">
                Maximum Attendees
              </label>
              <input
                type="number"
                id="maxAttendees"
                name="maxAttendees"
                min="1"
                value={formData.maxAttendees}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-apn-blue focus:border-apn-blue"
                placeholder="Leave blank for unlimited"
              />
            </div>
          </div>

          {/* Pricing Suggestions */}
          {formData.eventType && EVENT_PRICING[formData.eventType.toUpperCase()] && (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
              <h4 className="text-sm font-medium text-blue-900 mb-2">
                Suggested Pricing for {formData.eventType} Events
              </h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-blue-700">Member: </span>
                  <span className="font-medium">${EVENT_PRICING[formData.eventType.toUpperCase()].member}</span>
                </div>
                <div>
                  <span className="text-blue-700">Non-Member: </span>
                  <span className="font-medium">${EVENT_PRICING[formData.eventType.toUpperCase()].nonMember}</span>
                </div>
              </div>
              <p className="text-xs text-blue-600 mt-2">
                Prices have been auto-populated based on event type. You can modify them as needed.
              </p>
            </div>
          )}

          {/* Pricing */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="memberPrice" className="block text-sm font-medium text-gray-700">
                Member Price ($)
              </label>
              <input
                type="number"
                id="memberPrice"
                name="memberPrice"
                min="0"
                step="0.01"
                value={formData.memberPrice}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-apn-blue focus:border-apn-blue"
                placeholder="0.00"
              />
            </div>

            <div>
              <label htmlFor="nonMemberPrice" className="block text-sm font-medium text-gray-700">
                Non-Member Price ($)
              </label>
              <input
                type="number"
                id="nonMemberPrice"
                name="nonMemberPrice"
                min="0"
                step="0.01"
                value={formData.nonMemberPrice}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-apn-blue focus:border-apn-blue"
                placeholder="0.00"
              />
            </div>


          </div>

          {/* Payment Integration Options */}
          <div className="space-y-4">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="enablePayments"
                name="enablePayments"
                checked={formData.enablePayments}
                onChange={handleChange}
                className="h-4 w-4 text-apn-blue focus:ring-apn-blue border-gray-300 rounded"
              />
              <label htmlFor="enablePayments" className="ml-2 block text-sm text-gray-900">
                Enable automatic payment processing (Stripe & PayPal)
              </label>
            </div>

            {formData.enablePayments && (
              <div className="ml-6 space-y-2">
                <p className="text-sm text-gray-600">
                  When enabled, Stripe products and prices will be automatically created for this event,
                  allowing seamless payment processing without manual setup.
                </p>
                <div className="flex space-x-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.paymentMethods.includes('stripe')}
                      onChange={(e) => {
                        const methods = e.target.checked
                          ? [...formData.paymentMethods, 'stripe']
                          : formData.paymentMethods.filter(m => m !== 'stripe');
                        setFormData(prev => ({ ...prev, paymentMethods: methods }));
                      }}
                      className="h-4 w-4 text-apn-blue focus:ring-apn-blue border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">Stripe (Credit Cards)</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.paymentMethods.includes('paypal')}
                      onChange={(e) => {
                        const methods = e.target.checked
                          ? [...formData.paymentMethods, 'paypal']
                          : formData.paymentMethods.filter(m => m !== 'paypal');
                        setFormData(prev => ({ ...prev, paymentMethods: methods }));
                      }}
                      className="h-4 w-4 text-apn-blue focus:ring-apn-blue border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">PayPal</span>
                  </label>
                </div>
              </div>
            )}
          </div>

          {/* Registration Deadline */}
          <div>
            <label htmlFor="registrationDeadline" className="block text-sm font-medium text-gray-700">
              Registration Deadline
            </label>
            <input
              type="date"
              id="registrationDeadline"
              name="registrationDeadline"
              value={formData.registrationDeadline}
              onChange={handleChange}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-apn-blue focus:border-apn-blue"
            />
          </div>

          {/* Agenda */}
          <div>
            <label htmlFor="agenda" className="block text-sm font-medium text-gray-700">
              Event Agenda
            </label>
            <textarea
              id="agenda"
              name="agenda"
              rows="4"
              value={formData.agenda}
              onChange={handleChange}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-apn-blue focus:border-apn-blue"
              placeholder="Enter each agenda item on a new line..."
            />
            <p className="mt-1 text-sm text-gray-500">Enter each agenda item on a separate line</p>
          </div>

          {/* Speakers */}
          <div>
            <label htmlFor="speakers" className="block text-sm font-medium text-gray-700">
              Speakers
            </label>
            <textarea
              id="speakers"
              name="speakers"
              rows="3"
              value={formData.speakers}
              onChange={handleChange}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-apn-blue focus:border-apn-blue"
              placeholder="Enter each speaker on a new line..."
            />
            <p className="mt-1 text-sm text-gray-500">Enter each speaker on a separate line</p>
          </div>

          {/* Requirements */}
          <div>
            <label htmlFor="requirements" className="block text-sm font-medium text-gray-700">
              Requirements/Prerequisites
            </label>
            <textarea
              id="requirements"
              name="requirements"
              rows="3"
              value={formData.requirements}
              onChange={handleChange}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-apn-blue focus:border-apn-blue"
              placeholder="Enter any requirements or prerequisites..."
            />
          </div>

          {/* Submit Button */}
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate('/events')}
              className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || uploadingImage}
              className="px-6 py-2 bg-apn-blue text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {uploadingImage ? 'Uploading Image...' : loading ? 'Creating...' : 'Create Event'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
